"use client";

import React, { useEffect, useState } from "react";

type SignalRow = {
  symbol: string;
  date?: string;
  atr?: number;
  box_multiplier?: number;
  current_price?: number;
  renko: { 
    valid: boolean;
    trend: string; // "bullish", "bearish", "neutral"
    strength: number; // 0-100
    entry_price?: number;
    target?: number;
    stop_loss?: number;
  };
  pnf: { 
    valid: boolean;
    trend: string; // "bullish", "bearish", "neutral"
    trendline?: string;
    trendline_length?: number;
    breakout?: string;
    breakout_hits?: number;
    support_1?: number;
    support_2?: number;
    resistance_1?: number;
    resistance_2?: number;
    confidence: number; // 0-100
    matrix_columns: number;
  };
  turtle_strategy?: {
    combined_score: number;
    breakout_signal: boolean;
    trend_strength: number;
    has_trendline: boolean;
  };
};

export default function PointAndFigurePage() {
  const [rows, setRows] = useState<SignalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [minQuality, setMinQuality] = useState<number>(0);
  const [minRiskReward, setMinRiskReward] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`/api/outputs/renko_pnf.json`);
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data = await res.json();
        setRows(data.results || []);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Filter and sort rows based on criteria
  const filteredRows = React.useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const filtered = rows.filter(row => {
      // Quality filter (P&F confidence score)
      const quality = row.pnf.confidence || 0;
      if (quality < minQuality) return false;

      // Risk/Reward filter - calculate from available data
      const riskReward = row.renko.target && row.renko.stop_loss && row.current_price
        ? Math.abs((row.renko.target - row.current_price) / (row.current_price - row.renko.stop_loss))
        : 0;
      if (minRiskReward > 0 && riskReward < minRiskReward) return false;

      if (query) {
        const symbol = row.symbol?.toLowerCase() ?? "";
        if (!symbol.includes(query)) return false;
      }

      return true;
    });

    // Sort by turtle strategy score (if available), then by confidence
    filtered.sort((a, b) => {
      const scoreA = a.turtle_strategy?.combined_score ?? a.pnf.confidence ?? 0;
      const scoreB = b.turtle_strategy?.combined_score ?? b.pnf.confidence ?? 0;
      return scoreB - scoreA;
    });

    return filtered;
  }, [rows, minQuality, minRiskReward, searchTerm]);

  // Helper: Get quality score color
  const getQualityColor = (score?: number) => {
    if (!score) return "var(--color-text-muted)";
    if (score >= 70) return "var(--color-success)";
    if (score >= 50) return "#fbbf24";
    if (score >= 30) return "var(--color-accent)";
    return "var(--color-danger)";
  };

  return (
    <main>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Point & Figure Scanner</h1>
          <p className="page-subtitle">
            Classic P&F charting with 3-box reversal, support/resistance levels, and price targets
          </p>
          <div style={{ marginTop: "1rem", display: "flex", gap: "1.25rem", alignItems: "center", flexWrap: "wrap" }}>
            {/* Quality Filter */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <label style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", fontWeight: 500 }}>
                Min Quality:
              </label>
              <select 
                value={minQuality} 
                onChange={(e) => setMinQuality(Number(e.target.value))}
                style={{ 
                  padding: "0.35rem 0.6rem",
                  borderRadius: "6px",
                  border: "1px solid rgba(148, 163, 184, 0.3)",
                  background: "rgba(30, 41, 59, 0.5)",
                  color: "var(--color-text)",
                  cursor: "pointer",
                  fontSize: "0.85rem"
                }}
              >
                <option value={0}>All</option>
                <option value={30}>≥ 30 (Fair)</option>
                <option value={50}>≥ 50 (Good)</option>
                <option value={70}>≥ 70 (High)</option>
              </select>
            </div>

            {/* Risk/Reward Filter */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <label style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", fontWeight: 500 }}>
                Min R:R:
              </label>
              <select 
                value={minRiskReward} 
                onChange={(e) => setMinRiskReward(Number(e.target.value))}
                style={{ 
                  padding: "0.35rem 0.6rem",
                  borderRadius: "6px",
                  border: "1px solid rgba(148, 163, 184, 0.3)",
                  background: "rgba(30, 41, 59, 0.5)",
                  color: "var(--color-text)",
                  cursor: "pointer",
                  fontSize: "0.85rem"
                }}
              >
                <option value={0}>All</option>
                <option value={1}>≥ 1.0</option>
                <option value={1.5}>≥ 1.5</option>
                <option value={2}>≥ 2.0</option>
              </select>
            </div>

            {/* Search */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <label style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", fontWeight: 500 }}>
                Search:
              </label>
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Symbol"
                style={{
                  padding: "0.35rem 0.6rem",
                  borderRadius: "6px",
                  border: "1px solid rgba(148, 163, 184, 0.3)",
                  background: "rgba(30, 41, 59, 0.5)",
                  color: "var(--color-text)",
                  fontSize: "0.85rem",
                  minWidth: "140px",
                }}
              />
            </div>

            {/* Results Count */}
            <div style={{ 
              marginLeft: "auto", 
              color: "var(--color-text-muted)", 
              fontSize: "0.85rem",
              padding: "0.35rem 0.75rem",
              background: "rgba(59, 130, 246, 0.1)",
              borderRadius: "6px",
              border: "1px solid rgba(59, 130, 246, 0.2)"
            }}>
              Showing {filteredRows.length} of {rows.length} signals
            </div>
          </div>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          {loading && <div style={{ color: "var(--color-text-muted)" }}>Loading...</div>}
          {error && <div style={{ color: "#fca5a5" }}>{error}</div>}

          {!loading && !error && (
            <div style={{ overflowX: "auto" }}>
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Price</th>
                    <th>Direction</th>
                    <th>Boxes</th>
                    <th>Trend</th>
                    <th>Quality</th>
                    <th>Target</th>
                    <th>Support</th>
                    <th>Resistance</th>
                    <th>R:R</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, idx) => {
                    // Get trend direction with color
                    const trendDirection = row.pnf.trend === "bullish" ? "up" : row.pnf.trend === "bearish" ? "down" : "neutral";
                    const trendColor = row.pnf.trend === "bullish" ? "var(--color-success)" : row.pnf.trend === "bearish" ? "var(--color-danger)" : "var(--color-text-muted)";
                    
                    // Calculate risk/reward ratio (prefer Renko, fallback to P&F levels)
                    let riskReward: number | null = null;
                    if (row.renko.target && row.renko.stop_loss && row.current_price) {
                      const reward = Math.abs(row.renko.target - row.current_price);
                      const risk = Math.abs(row.current_price - row.renko.stop_loss);
                      riskReward = risk > 0 ? reward / risk : null;
                    } else if (row.current_price && row.pnf.support_1 && row.pnf.resistance_1) {
                      if (row.pnf.trend === "bearish") {
                        const reward = Math.abs(row.current_price - row.pnf.support_1);
                        const risk = Math.abs(row.pnf.resistance_1 - row.current_price);
                        riskReward = risk > 0 ? reward / risk : null;
                      } else {
                        const reward = Math.abs(row.pnf.resistance_1 - row.current_price);
                        const risk = Math.abs(row.current_price - row.pnf.support_1);
                        riskReward = risk > 0 ? reward / risk : null;
                      }
                    }

                    // Get turtle strategy score or use confidence
                    const scoreCandidate = row.turtle_strategy?.combined_score ?? 0;
                    const score = scoreCandidate > 0 ? scoreCandidate : (row.pnf.confidence ?? 0);
                    
                    const displayTarget = row.renko.target
                      ? { value: row.renko.target, source: "renko" as const }
                      : row.pnf.trend === "bearish"
                        ? (row.pnf.support_1 ? { value: row.pnf.support_1, source: "pnf" as const } : null)
                        : (row.pnf.resistance_1 ? { value: row.pnf.resistance_1, source: "pnf" as const } : null);
                    
                    return (
                      <tr key={row.symbol + "-" + idx}>
                        <td style={{ fontWeight: 600 }}>{row.symbol}</td>
                        
                        {/* Current Price */}
                        <td style={{ fontWeight: 500 }}>{row.current_price?.toFixed(2) ?? "—"}</td>

                        {/* Combined Trend Direction (Renko + P&F) */}
                        <td style={{ color: trendColor, fontWeight: 600, textTransform: "uppercase" }}>
                          {trendDirection}
                        </td>

                        {/* P&F Matrix Columns (Boxes) */}
                        <td style={{ fontWeight: 500 }}>{row.pnf.matrix_columns ?? "—"}</td>

                        {/* P&F Trend with Renko Strength */}
                        <td>
                          <span style={{ fontSize: "0.75rem" }}>
                            {row.pnf.trend === "bullish" ? "📈" : row.pnf.trend === "bearish" ? "📉" : "—"}
                            {" "}
                            {row.renko.strength ? `R${row.renko.strength}%` : "—"}
                          </span>
                        </td>

                        {/* Overall Quality Score */}
                        <td style={{ color: getQualityColor(score), fontWeight: 600 }}>
                          {score > 0 ? Math.round(score) : "—"}
                        </td>

                        {/* Renko Target with percentage */}
                        <td>
                          {displayTarget && row.current_price ? (
                            <>
                              {displayTarget.value.toFixed(2)}
                              <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                                ({((displayTarget.value - row.current_price) / row.current_price * 100).toFixed(1)}%)
                                {displayTarget.source === "pnf" ? " P&F" : ""}
                              </div>
                            </>
                          ) : "—"}
                        </td>

                        {/* Support (first level from P&F) */}
                        <td style={{ color: "var(--color-success)", fontSize: "0.85rem" }}>
                          {row.pnf.support_1?.toFixed(2) ?? "—"}
                        </td>

                        {/* Resistance (first level from P&F) */}
                        <td style={{ color: "var(--color-danger)", fontSize: "0.85rem" }}>
                          {row.pnf.resistance_1?.toFixed(2) ?? "—"}
                        </td>

                        {/* Risk/Reward Ratio */}
                        <td style={{
                          fontWeight: 600,
                          color: riskReward && riskReward >= 2 ? "var(--color-success)" : 
                                 riskReward && riskReward >= 1.5 ? "#fbbf24" : "var(--color-danger)"
                        }}>
                          {riskReward ? riskReward.toFixed(2) : "—"}
                        </td>

                        <td style={{ color: "var(--color-text-muted)", fontSize: "0.8rem" }}>
                          {row.date ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Explanation Section */}
          {!loading && !error && (
            <div style={{ 
              marginTop: "2.5rem", 
              padding: "1.5rem", 
              background: "rgba(30, 41, 59, 0.5)",
              borderRadius: "8px",
              border: "1px solid rgba(148, 163, 184, 0.2)"
            }}>
              <h3 style={{ 
                fontSize: "1.1rem", 
                fontWeight: 600, 
                marginBottom: "1rem",
                color: "var(--color-text)"
              }}>
                Understanding Point & Figure Charts
              </h3>
              
              <div style={{ 
                display: "grid", 
                gap: "1.25rem",
                fontSize: "0.9rem",
                lineHeight: 1.6,
                color: "rgba(241, 245, 249, 0.8)"
              }}>
                <div>
                  <strong style={{ color: "#3b82f6" }}>What is Point & Figure?</strong>
                  <p style={{ marginTop: "0.35rem", marginBottom: 0 }}>
                    P&F charts plot price movements using X&apos;s (upward moves) and O&apos;s (downward moves) while filtering out minor fluctuations. 
                    Uses <strong>3-box reversal rule</strong>: requires 3 boxes in opposite direction to create a new column.
                  </p>
                  <p style={{ marginTop: "0.5rem", marginBottom: 0 }}>
                    <strong>Boxes</strong> = Column height (higher = stronger trend). 
                    <strong>🔄 Symbol</strong> = Recent column reversal (potential entry signal).
                  </p>
                </div>

                <div>
                  <strong style={{ color: "#3b82f6" }}>Risk:Reward Ratio (R:R)</strong>
                  <p style={{ marginTop: "0.35rem", marginBottom: 0 }}>
                    Measures the potential reward versus the risk of a trade. Calculated as:{" "}
                    <code style={{ 
                      background: "rgba(59, 130, 246, 0.15)", 
                      padding: "2px 6px", 
                      borderRadius: "3px",
                      fontFamily: "monospace",
                      fontSize: "0.85em"
                    }}>
                      (Target - Current Price) / (Current Price - Stop Loss)
                    </code>
                  </p>
                  <p style={{ marginTop: "0.5rem", marginBottom: 0 }}>
                    <span style={{ color: "var(--color-success)" }}>≥ 2.0</span> = Excellent (reward is 2x the risk),{" "}
                    <span style={{ color: "#fbbf24" }}>≥ 1.5</span> = Good,{" "}
                    <span style={{ color: "var(--color-danger)" }}>&lt; 1.5</span> = Lower quality
                  </p>
                </div>

                <div>
                  <strong style={{ color: "#3b82f6" }}>Quality Score (0-100)</strong>
                  <p style={{ marginTop: "0.35rem", marginBottom: 0 }}>
                    Based on column height (boxes). Taller columns indicate stronger, more established trends.
                    Calculated as: <code style={{ background: "rgba(59, 130, 246, 0.15)", padding: "2px 6px", borderRadius: "3px" }}>boxes × 8</code>
                  </p>
                  <p style={{ marginTop: "0.5rem", marginBottom: 0 }}>
                    <span style={{ color: "var(--color-success)" }}>≥ 70</span> = High quality (strong trend),{" "}
                    <span style={{ color: "#fbbf24" }}>≥ 50</span> = Good,{" "}
                    <span style={{ color: "var(--color-accent)" }}>≥ 30</span> = Fair,{" "}
                    <span style={{ color: "var(--color-danger)" }}>&lt; 30</span> = Low quality
                  </p>
                </div>

                <div>
                  <strong style={{ color: "#3b82f6" }}>Trend Strength</strong>
                  <p style={{ marginTop: "0.35rem", marginBottom: 0 }}>
                    Classified based on column height: <strong>Very Strong</strong> (10+ boxes), 
                    <strong> Strong</strong> (8+ boxes), <strong> Moderate</strong> (5+ boxes), 
                    <strong> Weak</strong> (3+ boxes), <strong> Emerging</strong> (&lt;3 boxes)
                  </p>
                </div>

                <div>
                  <strong style={{ color: "#3b82f6" }}>Support & Resistance</strong>
                  <p style={{ marginTop: "0.35rem", marginBottom: 0 }}>
                    Key price levels from Point & Figure reversal points. <span style={{ color: "var(--color-success)" }}>Support</span> acts as a floor (buy zone), 
                    <span style={{ color: "var(--color-danger)" }}> Resistance</span> acts as a ceiling (sell zone). 
                    Stop losses should be placed slightly beyond these levels.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
